import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faImage, faSpinner } from '@/fontawesome';
import Modal from '@/components/ui/Modal';
import ThumbnailUpload from '@/components/ui/ThumbnailUpload';
import { APIRoutes } from '@/services/api.routes';
import { handleAPIError, showSuccessToast } from '@/utils/toasts';
import type { CourseID, ModuleID, VideoID } from '@/models';

interface ThumbnailEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onThumbnailUpdated: () => void;
  type: 'course' | 'module' | 'video';
  courseId?: CourseID;
  moduleId?: ModuleID;
  videoId?: VideoID;
  currentThumbnailUrl?: string;
}

export default function ThumbnailEditModal({
  isOpen,
  onClose,
  onThumbnailUpdated,
  type,
  courseId,
  moduleId,
  videoId,
  currentThumbnailUrl
}: ThumbnailEditModalProps) {
  const [thumbnailData, setThumbnailData] = useState<{ file: File; base64: string } | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!thumbnailData?.file) {
      onClose();
      return;
    }

    try {
      setLoading(true);
      
      if (type === 'course' && courseId) {
        await APIRoutes.uploadCourseThumbnail(courseId, thumbnailData.file);
      } else if (type === 'module' && moduleId) {
        await APIRoutes.uploadModuleThumbnail(moduleId, thumbnailData.file);
      } else if (type === 'video' && videoId) {
        await APIRoutes.uploadVideoThumbnail(videoId, thumbnailData.file);
      } else {
        throw new Error('Invalid parameters for thumbnail upload');
      }
      
      showSuccessToast('Thumbnail wurde erfolgreich aktualisiert');
      onThumbnailUpdated();
      onClose();
      
      // Reset thumbnail data
      setThumbnailData(null);
    } catch (err) {
      console.error('Error uploading thumbnail:', err);
      handleAPIError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setThumbnailData(null);
      onClose();
    }
  };

  const getTitle = () => {
    switch (type) {
      case 'course': return 'Kurs-Thumbnail bearbeiten';
      case 'module': return 'Modul-Thumbnail bearbeiten';
      case 'video': return 'Video-Thumbnail bearbeiten';
      default: return 'Thumbnail bearbeiten';
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={getTitle()}
      icon={<FontAwesomeIcon icon={faImage} className="w-5 h-5 text-blue-600" />}
      closeDisabled={loading}
      maxWidth="md"
    >
      <form onSubmit={handleSubmit} className="p-6 space-y-6">
        {currentThumbnailUrl && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Aktuelles Thumbnail
            </label>
            <div className="w-32 h-32 border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
              <img 
                src={currentThumbnailUrl} 
                alt="Current thumbnail" 
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        )}

        <ThumbnailUpload
          onThumbnailChange={setThumbnailData}
          disabled={loading}
          existingThumbnail={currentThumbnailUrl}
        />

        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-600">
          <button
            type="button"
            onClick={handleClose}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 cursor-pointer"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={loading || !thumbnailData?.file}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 cursor-pointer"
          >
            {loading ? (
              <>
                <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 mr-2 animate-spin" />
                Aktualisiere...
              </>
            ) : (
              'Thumbnail aktualisieren'
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
}